"use client";

import { useEffect, useState } from "react";
import { vraiSolde } from "@/lib/tools/vraiSolde";
import { dateLongue, euros } from "@/lib/tools/dossierTextes";
import { readLocal, writeLocal } from "@/helpers/safeStorage";
import { pourcent, remplir } from "./situationTextes";

/**
 * « Votre vrai solde », sur la page de situation : le solde du compte, moins
 * ce qui partira à l'Urssaf, aux impôts et à la CFE (lib/tools/vraiSolde.js).
 *
 * LE SOLDE NE SE GARDE PAS. C'est le chiffre le plus intime de la page : il
 * reste dans le champ, le temps de la visite, et ne rejoint ni le dossier ni
 * le compte. Seule la périodicité de déclaration est retenue, sur l'appareil.
 */

const CLE = "believemy-vrai-solde";

const T = {
    fr: {
        kicker: "Votre vrai solde",
        titre: "Sur votre compte, combien est vraiment à vous ?",
        solde: "Le solde de votre compte pro aujourd'hui",
        periodicite: "Vous déclarez à l'Urssaf",
        mensuelle: "Chaque mois",
        trimestrielle: "Chaque trimestre",
        urssaf: "Urssaf",
        urssafDetail: "{taux} de {base} encaissés, à déclarer avant le {date}",
        estime: "Estimé sur votre chiffre d'affaires annuel. Donnez vos encaissements mois par mois pour un chiffre exact.",
        dejaMois: "J'ai déjà déclaré et payé le mois dernier",
        dejaTrimestre: "J'ai déjà déclaré et payé le trimestre dernier",
        impot: "Impôt sur le revenu",
        impotDetail: "Votre année de création : rien n'est prélevé. Il tombera en septembre {annee}.",
        impotDetailDeuxieme: "Vos acomptes ne démarrent qu'en septembre : l'impôt de vos premiers mois de l'année tombera en septembre {annee}.",
        impotAcomptes: "L'impôt sur le revenu est déjà prélevé chaque mois par vos acomptes : rien de plus à garder.",
        cfe: "CFE",
        cfeDetail: "À payer le {date}. Tapez le montant de votre avis pour le compter.",
        cfeMontant: "Montant de votre avis de CFE",
        tva: "La TVA facturée depuis le {date} n'est pas à vous : gardez-la de côté pour la déclarer.",
        aVous: "Vraiment à vous",
        part: "{part} de votre compte",
        rappel: "Revenez le recalculer chaque mois : vous ne dépenserez plus l'argent de l'Urssaf sans le savoir. Ce solde n'est gardé nulle part.",
        horsAcre: "Hors ACRE. Vous l'avez ? Indiquez-le sur la fiche de votre entreprise, en haut de la page des outils.",
        avecAcre: "ACRE comprise jusqu'au {date}.",
        nonMicro: "Le vrai solde se calcule pour l'instant en micro-entreprise. Au réel ou en société, le compte suit d'autres règles.",
        manque: "Il faut votre activité et votre chiffre d'affaires pour le calculer.",
    },
    en: {
        kicker: "Your real balance",
        titre: "How much of your account is really yours?",
        solde: "Your business account balance today",
        periodicite: "You declare to Urssaf",
        mensuelle: "Every month",
        trimestrielle: "Every quarter",
        urssaf: "Urssaf",
        urssafDetail: "{taux} of {base} brought in, to declare before {date}",
        estime: "Estimated from your annual revenue. Enter your takings month by month for an exact figure.",
        dejaMois: "I have already declared and paid last month",
        dejaTrimestre: "I have already declared and paid last quarter",
        impot: "Income tax",
        impotDetail: "Your first year: nothing is withheld. It will fall due in September {annee}.",
        impotDetailDeuxieme: "Your instalments only start in September: the tax on your first months of the year will fall due in September {annee}.",
        impotAcomptes: "Income tax is already withheld every month through your instalments: nothing more to set aside.",
        cfe: "CFE",
        cfeDetail: "Due on {date}. Type the amount on your notice to count it.",
        cfeMontant: "Amount on your CFE notice",
        tva: "The VAT you have invoiced since {date} is not yours: set it aside to declare it.",
        aVous: "Really yours",
        part: "{part} of your account",
        rappel: "Come back each month to work it out again: you will no longer spend Urssaf's money without knowing. This balance is not stored anywhere.",
        horsAcre: "Excluding ACRE. Have it? Say so on your company card, at the top of the tools page.",
        avecAcre: "ACRE included until {date}.",
        nonMicro: "The real balance only works for micro-enterprises for now. On actual expenses or as a company, the account follows other rules.",
        manque: "Your activity and your revenue are needed to work it out.",
    },
};

const COULEURS = { urssaf: "bg-orange-400", impot: "bg-amber-400", cfe: "bg-violet-400" };

function lireMontant(texte) {
    const chiffres = String(texte ?? "").replace(/[.,]\d{1,2}\s*€?$/, "").replace(/\D/g, "");
    return chiffres === "" ? null : Number(chiffres);
}

export default function ToolsVraiSolde({ lang = "fr", dossier, aujourdhui, onPart }) {
    const t = T[lang] || T.fr;
    const [saisie, setSaisie] = useState("");
    const [cfe, setCfe] = useState("");
    const [dejaDeclare, setDejaDeclare] = useState(false);
    const [periodicite, setPeriodicite] = useState(() => {
        try {
            return readLocal(CLE) === "trimestrielle" ? "trimestrielle" : "mensuelle";
        } catch (e) {
            return "mensuelle";
        }
    });
    const solde = lireMontant(saisie);
    const r = vraiSolde(dossier, { solde: solde ?? 0, aujourdhui, periodicite, dejaDeclare, cfe: lireMontant(cfe) });

    // La part est remontée à la page, pour la carte à partager.
    const part = r.possible && solde ? r.part : null;
    useEffect(() => {
        onPart?.(part);
    }, [part, onPart]);

    const choisirPeriodicite = (p) => {
        setPeriodicite(p);
        try {
            writeLocal(CLE, p);
        } catch (e) {
            // Stockage indisponible : le choix vaut pour la visite.
        }
    };

    const carte = "rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 sm:p-6";
    // HORS MICRO, RIEN À MONTRER : une carte entière pour dire « pas pour
    // vous » n'apprenait rien au lecteur. Elle disparaît.
    if (!r.possible && r.raison === "statut") return null;
    if (!r.possible) {
        return (
            <section className={carte} aria-labelledby="vrai-solde-titre">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.kicker}</div>
                <h2 id="vrai-solde-titre" className="mt-2 mb-2 text-lg sm:text-xl font-bold dark:text-white">{t.titre}</h2>
                <p className="mb-0 text-sm text-gray-600 dark:text-lightBlue">{r.raison === "statut" ? t.nonMicro : t.manque}</p>
            </section>
        );
    }

    const comptees = r.lignes.filter((l) => l.compte && l.montant > 0);
    const largeur = (m) => (solde > 0 ? `${Math.max(1.5, Math.min(100, (m / solde) * 100))}%` : "0%");

    return (
        <section className={carte} aria-labelledby="vrai-solde-titre">
            <div className="text-xs font-bold uppercase tracking-wider text-[#B83C12] dark:text-[#FF8A5B]">{t.kicker}</div>
            <h2 id="vrai-solde-titre" className="mt-2 mb-4 text-xl sm:text-2xl font-bold font-sourceSans dark:text-white">{t.titre}</h2>

            <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold dark:text-white">{t.solde}</span>
                    <span className="flex items-center rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-4 focus-within:ring-2 focus-within:ring-[#D04515]/30">
                        <input
                            inputMode="numeric"
                            value={saisie}
                            onChange={(e) => setSaisie(e.target.value)}
                            placeholder="8 400"
                            className="min-h-[52px] min-w-0 flex-1 bg-transparent text-xl font-bold tabular-nums text-gray-900 dark:text-white focus:outline-hidden"
                        />
                        <span className="text-lg font-bold text-gray-500">€</span>
                    </span>
                </label>
                <fieldset className="m-0 min-w-0 border-0 p-0">
                    <legend className="mb-1.5 text-sm font-semibold dark:text-white">{t.periodicite}</legend>
                    <div className="flex gap-2">
                        {["mensuelle", "trimestrielle"].map((p) => (
                            <button
                                key={p}
                                type="button"
                                aria-pressed={periodicite === p}
                                onClick={() => choisirPeriodicite(p)}
                                className={`min-h-[52px] flex-1 rounded-xl border-2 px-3 text-sm font-semibold transition-colors duration-200 ${periodicite === p ? "border-[#D04515] bg-[#fff8f4] text-gray-900 dark:bg-[#D04515]/10 dark:text-white" : "border-gray-200 text-gray-700 dark:border-white/10 dark:text-lightBlue"}`}
                            >
                                {t[p]}
                            </button>
                        ))}
                    </div>
                </fieldset>
            </div>

            {solde > 0 && (
                <div className="mt-5 flex h-4 overflow-hidden rounded-full bg-green-600/90" aria-hidden="true">
                    {comptees.map((l) => (
                        <div key={l.cle} className={`${COULEURS[l.cle]} h-full`} style={{ width: largeur(l.montant) }} />
                    ))}
                </div>
            )}

            <ul className="mt-4 mb-0 list-none divide-y divide-gray-200/70 dark:divide-white/10 p-0">
                {r.lignes.map((l) => {
                    if (l.cle === "urssaf") {
                        return (
                            <li key={l.cle} className="py-3">
                                <div className="flex items-start justify-between gap-4">
                                    <span className="flex items-start gap-2.5">
                                        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm ${COULEURS.urssaf}`} />
                                        <span>
                                            <span className="block font-semibold dark:text-white">{t.urssaf}</span>
                                            <span className="block text-sm text-gray-600 dark:text-lightBlue">
                                                {remplir(t.urssafDetail, {
                                                    taux: new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR", { style: "percent", maximumFractionDigits: 1 }).format(l.taux),
                                                    base: euros(l.base, lang),
                                                    date: dateLongue(l.echeance, lang),
                                                })}
                                            </span>
                                            {l.estime && <span className="mt-1 block text-xs text-gray-500 dark:text-lightBlue/70">{t.estime}</span>}
                                        </span>
                                    </span>
                                    <span className="shrink-0 font-bold tabular-nums dark:text-white">{euros(l.montant, lang)}</span>
                                </div>
                                <label className="mt-2 ml-5 flex min-h-[44px] items-center gap-2 text-sm text-gray-600 dark:text-lightBlue">
                                    <input type="checkbox" checked={dejaDeclare} onChange={(e) => setDejaDeclare(e.target.checked)} className="h-4 w-4 accent-[#D04515]" />
                                    {periodicite === "trimestrielle" ? t.dejaTrimestre : t.dejaMois}
                                </label>
                            </li>
                        );
                    }
                    if (l.cle === "impot") {
                        return (
                            <li key={l.cle} className="flex items-start justify-between gap-4 py-3">
                                <span className="flex items-start gap-2.5">
                                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm ${COULEURS.impot}`} />
                                    <span>
                                        <span className="block font-semibold dark:text-white">{t.impot}</span>
                                        <span className="block text-sm text-gray-600 dark:text-lightBlue">{remplir(l.deuxieme ? t.impotDetailDeuxieme : t.impotDetail, { annee: l.echeance.slice(0, 4) })}</span>
                                    </span>
                                </span>
                                <span className="shrink-0 font-bold tabular-nums dark:text-white">{euros(l.montant, lang)}</span>
                            </li>
                        );
                    }
                    if (l.cle === "cfe") {
                        return (
                            <li key={l.cle} className="py-3">
                                <div className="flex items-start justify-between gap-4">
                                    <span className="flex items-start gap-2.5">
                                        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm ${COULEURS.cfe}`} />
                                        <span>
                                            <span className="block font-semibold dark:text-white">{t.cfe}</span>
                                            <span className="block text-sm text-gray-600 dark:text-lightBlue">{remplir(t.cfeDetail, { date: dateLongue(l.echeance, lang) })}</span>
                                        </span>
                                    </span>
                                    <label className="flex shrink-0 items-center gap-1">
                                        <span className="sr-only">{t.cfeMontant}</span>
                                        <input
                                            inputMode="numeric"
                                            value={cfe}
                                            onChange={(e) => setCfe(e.target.value)}
                                            placeholder="0"
                                            className="min-h-[44px] w-24 rounded-lg border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-2 text-right font-bold tabular-nums dark:text-white"
                                        />
                                        <span className="font-bold text-gray-500">€</span>
                                    </label>
                                </div>
                            </li>
                        );
                    }
                    return (
                        <li key={l.cle} className="py-3 text-sm text-gray-600 dark:text-lightBlue">
                            {l.cle === "tva" ? remplir(t.tva, { date: dateLongue(l.echeance, lang) }) : t.impotAcomptes}
                        </li>
                    );
                })}
            </ul>

            {solde > 0 && (
                <div className="mt-2 flex items-end justify-between gap-4 border-t-2 border-gray-900 dark:border-white pt-3">
                    <span>
                        <span className="block text-lg font-bold text-green-700 dark:text-green-400">{t.aVous}</span>
                        {r.part !== null && <span className="block text-sm text-gray-500 dark:text-lightBlue/70">{remplir(t.part, { part: pourcent(r.part, lang) })}</span>}
                    </span>
                    <span className="text-3xl font-bold tabular-nums font-sourceSans text-green-700 dark:text-green-400">{euros(r.aVous, lang)}</span>
                </div>
            )}

            <p className="mt-4 mb-0 rounded-xl bg-blue-50 dark:bg-blue-500/10 px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
                {/* L'ACRE du dossier : comprise, ou signalée comme absente du calcul. */}
                {r.acre ? `${t.avecAcre.replace("{date}", new Date(`${r.acre.fin}T12:00:00`).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" }))} ` : r.acre === null ? `${t.horsAcre} ` : ""}
                {t.rappel}
            </p>
        </section>
    );
}
